import React, { useEffect, useId, useRef, useState } from "react";
import { AnimatePresence, motion, useInView, useReducedMotion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRightIcon, ArrowUpRightIcon } from "lucide-react";
import { currentWork, type CurrentWorkItem } from "../data/currentWork";
import { EYEBROW, H3 } from "../utils/typography";
type CurrentlyWorkingOnProps = {
  /** Seconds to wait before entering, so it follows the hero headline */
  delay?: number;
};
const ease = [0.23, 1, 0.32, 1] as const;
export function CurrentlyWorkingOn({
  delay = 0
}: CurrentlyWorkingOnProps) {
  const reduce = useReducedMotion();
  const ref = useRef<HTMLElement>(null);
  const inView = useInView(ref, {
    margin: "-10% 0px"
  });
  const animate = !reduce && inView;
  return <motion.section ref={ref} id="now" aria-labelledby="now-heading" className="mt-20 scroll-mt-28 md:mt-28 md:scroll-mt-36" initial={reduce ? false : {
    opacity: 0,
    y: 16
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.3,
    ease,
    delay
  }} data-id="element-192" data-mp="1leo3ii7knxkw">
      <h2 id="now-heading" className={`${EYEBROW} flex items-center gap-3 text-rust`} data-id="element-193" data-mp="17rp6o1ft1jxz">
        <span className="relative flex h-2.5 w-2.5" aria-hidden="true" data-id="element-194" data-mp="trebwnbdjl7j">
          {!reduce && <motion.span className="absolute inline-flex h-full w-full rounded-full bg-mustard" animate={{
          scale: [1, 1.8],
          opacity: [0.35, 0]
        }} transition={{
          duration: 2.4,
          ease: "easeOut",
          repeat: Infinity,
          repeatDelay: 1.2
        }} data-id="element-195" data-mp="xszpkavqg3is" />}
          <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-mustard" data-id="element-196" data-mp="a3f5fonuf07x" />
        </span>
        Currently working on
      </h2>

      <ul className="mt-6 grid grid-cols-1 gap-6 md:mt-8 md:grid-cols-2" data-id="element-197" data-mp="f3jhn2hpx8sc">
        {currentWork.map((item, i) => <motion.li key={item.id} className="flex" initial={reduce ? false : {
        opacity: 0,
        y: 16
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.3,
        ease,
        delay: delay + 0.08 + i * 0.06
      }} data-id="element-198" data-mp="138sq4zyy49t" data-mpi={i}>
            <WorkItem item={item} animate={animate} offset={i * 0.9} data-id="element-199" data-mp="22z7weh2r9e6s" />
          </motion.li>)}
      </ul>

      <a href="#contact" className="group mt-8 inline-flex min-h-12 items-center gap-3 rounded-full bg-dusty py-2 pl-6 pr-2 text-sm font-medium uppercase tracking-wide text-cream transition-colors duration-150 ease-out hover:bg-espresso focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-dusty focus-visible:ring-offset-4 focus-visible:ring-offset-cream" data-id="element-200" data-mp="1y1zdufdp3850">
        Curious about any of these? Ask me
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-cream text-dusty transition-colors duration-150 ease-out group-hover:text-espresso" data-id="element-201" data-mp="2hr01m08u5bus">
          <ArrowRightIcon className="h-4 w-4 transition-transform duration-150 ease-out group-hover:translate-x-0.5" aria-hidden="true" data-id="element-202" data-mp="16bbf19zhueui" />
        </span>
      </a>
    </motion.section>;
}
function WorkItem({
  item,
  animate,
  offset
}: {
  item: CurrentWorkItem;
  animate: boolean;
  offset: number;
}) {
  const linked = Boolean(item.href);
  const card = <article className={`flex h-full w-full flex-col rounded-3xl bg-paper p-6 md:p-8 ${linked ? "transition-colors duration-150 ease-out group-hover:bg-[#e9e0cf]" : ""}`} data-id="element-203" data-mp="3lxdxjrytyg6w">
      <div className="flex items-start justify-between gap-4" data-id="element-204" data-mp="3vo8kyzntbltx">
        <p className={`${EYEBROW} text-espresso/70`} data-id="element-205" data-mp="4hxntelxb57v">
          {item.org}
          {item.role && <>
              {" "}
              <span aria-hidden="true" data-id="element-206" data-mp="9i1z8q7z59r8">·</span> {item.role}
            </>}
        </p>
        {linked && <span aria-hidden="true" className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-rust text-cream transition-colors duration-150 ease-out group-hover:bg-espresso" data-id="element-207" data-mp="dh17ajp05mmw">
            <ArrowUpRightIcon className="h-4 w-4 transition-transform duration-150 ease-out group-hover:-translate-y-0.5 group-hover:translate-x-0.5" data-id="element-208" data-mp="1jzsngpztxbct" />
          </span>}
      </div>
      <h3 className={`mt-3 ${H3} text-espresso`} data-id="element-209" data-mp="1tyhxjwlhhrii">{item.title}</h3>
      <p className="mt-3 max-w-md text-base leading-relaxed text-espresso/80" data-id="element-210" data-mp="1txq873ihcq0o">{item.summary}</p>

      <div className="mt-auto pt-8" data-id="element-211" data-mp="1p07xb7kwk0q1">
        <p className={`${EYEBROW} text-espresso/70`} data-id="element-212" data-mp="119komk35x72f">{item.label}</p>
        <div className="mt-3" data-id="element-213" data-mp="117rgt1gtcgsu">
          {item.visual === "questions" && <RotatingQuestion items={item.items} animate={animate} offset={offset} data-id="element-214" data-mp="wbsox3wa0gno" />}
          {item.visual === "cycle" && <SprintCycle items={item.items} animate={animate} offset={offset} data-id="element-215" data-mp="1b189u394lrvg" />}
          {item.visual === "flow" && <DataFlow items={item.items} animate={animate} data-id="element-216" data-mp="167ccn30os91c" />}
          {item.visual === "assemble" && <Assemble items={item.items} animate={animate} offset={offset} data-id="element-217" data-mp="ihrvmpo3flup" />}
        </div>
      </div>
      {linked && <span className="sr-only" data-id="element-218" data-mp="djzt71ptohc9">Read the case study</span>}
    </article>;
  if (!item.href) return card;
  return <Link to={item.href} className="group flex w-full rounded-3xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rust focus-visible:ring-offset-4 focus-visible:ring-offset-cream" data-id="element-219" data-mp="2p5s82doua4cy">
      {card}
    </Link>;
}

/** Cycles index every `interval` ms while animating; starts after `offset` seconds. */
function useCycle(length: number, animate: boolean, offset: number, interval: number) {
  const [index, setIndex] = useState(0);
  useEffect(() => {
    if (!animate) return;
    let timer: number | undefined;
    const start = window.setTimeout(() => {
      timer = window.setInterval(() => setIndex(i => (i + 1) % length), interval);
    }, offset * 1000);
    return () => {
      window.clearTimeout(start);
      if (timer) window.clearInterval(timer);
    };
  }, [animate, length, offset, interval]);
  return index;
}
function RotatingQuestion({
  items,
  animate,
  offset
}: {
  items: string[];
  animate: boolean;
  offset: number;
}) {
  const index = useCycle(items.length, animate, offset, 5200);
  return <div className="rounded-2xl bg-espresso px-5 py-4" data-id="element-220" data-mp="3p8gixpxdb98s">
      {/* Screen readers get the full list once, not a moving target */}
      <ul className="sr-only" data-id="element-221" data-mp="3kevvusxbbzod">
        {items.map(q => <li key={q} data-id="element-222" data-mp="3ge257rshk6rs">{q}</li>)}
      </ul>
      <div aria-hidden="true" className="relative h-14 overflow-hidden" data-id="element-223" data-mp="3gdakjc0fftga">
        <AnimatePresence initial={false} mode="popLayout" data-id="element-224" data-mp="3bhlh2xladsep">
          <motion.p key={items[index]} className="absolute inset-x-0 top-0 text-base font-medium leading-snug text-cream" initial={{
          y: 8,
          opacity: 0
        }} animate={{
          y: 0,
          opacity: 1
        }} exit={{
          y: -8,
          opacity: 0
        }} transition={{
          duration: 0.3,
          ease
        }} data-id="element-225" data-mp="36ge9ayj58t81">
            {items[index]}
          </motion.p>
        </AnimatePresence>
      </div>
    </div>;
}
function SprintCycle({
  items,
  animate,
  offset
}: {
  items: string[];
  animate: boolean;
  offset: number;
}) {
  const index = useCycle(items.length, animate, offset, 3000);
  const layoutId = useId();
  return <ol className="grid grid-cols-2 gap-2" data-id="element-226" data-mp="2ijrf8om2nf7f">
      {items.map((step, i) => {
      const active = animate && i === index;
      return <li key={step} className="relative" data-id="element-227" data-mp="2rdnynvnuqdj5" data-mpi={i}>
            {active && <motion.span layoutId={layoutId} aria-hidden="true" className="absolute inset-0 rounded-full bg-espresso" transition={{
          duration: 0.25,
          ease
        }} data-id="element-228" data-mp="2rfqnwdh6k2mt" />}
            <span className={`relative flex h-10 items-center justify-center rounded-full text-sm font-medium uppercase tracking-wide ring-1 transition-colors duration-200 ease-out ${active ? "text-cream ring-transparent" : "text-espresso ring-espresso/20"}`} data-id="element-229" data-mp="23na33v625wjt">
              {step}
            </span>
          </li>;
    })}
    </ol>;
}

/** Reusable sections settle into place one by one, like a page being composed from existing parts. */
function Assemble({
  items,
  animate,
  offset
}: {
  items: string[];
  animate: boolean;
  offset: number;
}) {
  // Headline, image, copy carry from the printed piece onto the screen, then a beat with all in place
  const step = useCycle(4, animate, offset, 1800);
  const shown = (i: number) => !animate || i < step ? "opacity-100" : "opacity-20";
  const fade = "transition-opacity duration-300 ease-out";
  const [printLabel, webLabel] = items;
  return <div className="flex items-end gap-3" data-id="element-230" data-mp="3ngxdt5gko1bw">
      <p className="sr-only" data-id="element-231" data-mp="3nfw3x4balkvr">The printed campaign pieces carried onto the website.</p>

      {/* Printed piece: portrait sheet, always complete */}
      <figure aria-hidden="true" className="shrink-0" data-id="element-232" data-mp="1t3698kd3o7nb">
        <div className="flex h-[5.5rem] w-16 flex-col gap-1.5 rounded-md bg-cream p-2 shadow-[0_6px_14px_-8px_rgba(29,24,20,0.35)] ring-1 ring-espresso/10" data-id="element-233" data-mp="1t3oci0y6jm4q">
          <span className="h-1.5 w-3/4 rounded-full bg-mustard" data-id="element-234" data-mp="1ex48bicxn5y8" />
          <span className="h-7 rounded-sm bg-espresso/80" data-id="element-235" data-mp="1ezqpm9pmkztn" />
          <span className="h-1 w-full rounded-full bg-espresso/25" data-id="element-236" data-mp="1iy6d0epmca7l" />
          <span className="h-1 w-2/3 rounded-full bg-espresso/25" data-id="element-237" data-mp="1582x05hc1cql" />
        </div>
        <figcaption className="mt-2 text-xs font-medium uppercase tracking-wide text-espresso/60" data-id="element-238" data-mp="1a6xmtrdpw1k7">{printLabel}</figcaption>
      </figure>

      <ArrowRightIcon aria-hidden="true" className="mb-12 h-4 w-4 shrink-0 text-espresso/35" data-id="element-239" data-mp="v9dj0nsk4cfm" />

      {/* Screen: the same pieces settle in one at a time */}
      <figure aria-hidden="true" className="min-w-0 flex-1" data-id="element-240" data-mp="106djmj6n0mzc">
        <div className="flex h-[5.5rem] flex-col overflow-hidden rounded-lg bg-cream shadow-[0_6px_14px_-8px_rgba(29,24,20,0.35)] ring-1 ring-espresso/10" data-id="element-241" data-mp="maji4l870zw9">
          <div className="flex gap-1 border-b border-espresso/10 px-2 py-1.5" data-id="element-242" data-mp="2gn9erapy4n7c">
            <span className="h-1.5 w-1.5 rounded-full bg-espresso/20" data-id="element-243" data-mp="2lm2r2zthy85x" />
            <span className="h-1.5 w-1.5 rounded-full bg-espresso/20" data-id="element-244" data-mp="2llb6ek1ftuuf" />
            <span className="h-1.5 w-1.5 rounded-full bg-espresso/20" data-id="element-245" data-mp="eenlkp376hvs" />
          </div>
          <div className="grid flex-1 grid-cols-[minmax(0,1fr)_40%] gap-2 p-2" data-id="element-246" data-mp="j6ouc115pqh2">
            <div className="flex flex-col gap-1.5" data-id="element-247" data-mp="5fad9zqt7zcn">
              <span className={`h-1.5 w-3/4 rounded-full bg-mustard ${fade} ${shown(0)}`} data-id="element-248" data-mp="5gv7fo97pg9z" />
              <span className={`flex flex-col gap-1.5 pt-1 ${fade} ${shown(2)}`} data-id="element-249" data-mp="9h5b2u9j7ulo">
                <span className="h-1 w-full rounded-full bg-espresso/25" data-id="element-250" data-mp="x79gn76a64fb" />
                <span className="h-1 w-5/6 rounded-full bg-espresso/25" data-id="element-251" data-mp="125t3d9sp7k8q" />
                <span className="h-1 w-2/3 rounded-full bg-espresso/25" data-id="element-252" data-mp="122x1jadqcbf0" />
              </span>
            </div>
            <span className={`rounded-sm bg-espresso/80 ${fade} ${shown(1)}`} data-id="element-253" data-mp="t4crexm8ja2e" />
          </div>
        </div>
        <figcaption className="mt-2 text-xs font-medium uppercase tracking-wide text-espresso/60" data-id="element-254" data-mp="38w0piwczj8ms">{webLabel}</figcaption>
      </figure>
    </div>;
}

/** Three nodes joined by tracks; a mustard pulse carries data from GA4 through the MCP to Claude. */
function DataFlow({
  items,
  animate
}: {
  items: string[];
  animate: boolean;
}) {
  return <div className="flex h-[5.5rem] items-center rounded-2xl bg-espresso px-4" data-id="element-255" data-mp="31bolql7y6pd2">
      <p className="sr-only" data-id="element-256" data-mp="3fay49rjmlvk7">{items.join(" connects through ")}</p>
      <div aria-hidden="true" className="flex w-full items-center" data-id="element-257" data-mp="3ad61u3lcur1r">
        {items.map((node, i) => <React.Fragment key={node} data-id="element-258" data-mp="2mmshz0cxbyi9" data-mpi={i}>
            <span className={`flex h-10 shrink-0 items-center justify-center whitespace-nowrap rounded-full px-3 text-sm sm:px-4 font-medium uppercase tracking-wide ${i === 1 ? "bg-mustard text-espresso" : "text-cream ring-1 ring-cream/25"}`} data-id="element-259" data-mp="2mnv7p4yifuwl">
              {node}
            </span>
            {i < items.length - 1 && <span className="relative mx-2 h-1.5 flex-1 overflow-hidden" data-id="element-260" data-mp="23vsnxkqknyi4">
                <span className="absolute inset-x-0 top-1/2 h-px -translate-y-1/2 bg-cream/20" data-id="element-261" data-mp="1yy99ajpypjpn" />
                {animate && <motion.span className="absolute top-1/2 h-1 w-4 -translate-y-1/2 rounded-full bg-mustard/70" initial={{
            left: "-25%"
          }} animate={{
            left: "110%"
          }} transition={{
            duration: 1.6,
            ease: "linear",
            repeat: Infinity,
            repeatDelay: 2.4,
            delay: i * 1.6
          }} data-id="element-262" data-mp="2ttev6h8dzpk" />}
              </span>}
          </React.Fragment>)}
      </div>
    </div>;
}